import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { t } from 'react-switch-lang';
import moment from 'moment';
import { handleAPIError, useCognito } from '../utils/Cognito';
import { getDefaultDateRange, parseStringToUTCDateTime } from '../utils/ReportsHelpers';
import { clearPaymentsReport, setPaymentsReport, setPaymentsReportLoading } from '../redux/actions/ReportActions';
import { REPORTS, TIMEZONE_CODES } from '../utils/Constants';
import { setScreenReaderAlert } from '../utils/Accessibility';
import { events, logAmpEvent } from '../utils/Amplitude';

function useReportData(reportType, dateRange) {
  const cognito = useCognito();
  const dispatch = useDispatch();

  const defaultDateRange = getDefaultDateRange(reportType);

  const call = cognito.getPaymentsReport;
  const reduxState = 'paymentsReport';
  const clearAction = clearPaymentsReport;
  const setAction = setPaymentsReport;
  const loadAction = setPaymentsReportLoading;

  const reportRecords = useSelector((state) => state.reports[reduxState].records);
  const currentPartner = useSelector((state) => state.partner.currentPartner);
  const loading = useSelector((state) => state.reports[reduxState].loading);

  const [longLoadTime, setLongLoadTime] = useState(false);
  const [error, setError] = useState(null);

  const LONG_LOAD_TIME_MESSAGE_DELAY_SECONDS = 10;

  const getReportData = useCallback(async ({ startDate, endDate, timezone }) => {
    async function fetchReportData(req) {
      const res = await call(req);
      if (!handleAPIError(null, res, setError)) {
        // if not in maintenance mode then send to error page
        dispatch(loadAction(false));
        setLongLoadTime(false);
        return null;
      }
      return res;
    }

    setLongLoadTime(false);
    setTimeout(() => {
      setLongLoadTime(true);
    }, LONG_LOAD_TIME_MESSAGE_DELAY_SECONDS * 1000); // delay before showing a long load message

    if (loading || !currentPartner) return;

    dispatch(loadAction(true));
    setError(null);

    const timezoneOffsetMinutes = TIMEZONE_CODES[timezone] * 60;
    const startDateTime = parseStringToUTCDateTime(startDate);
    const endDateTime = parseStringToUTCDateTime(endDate);

    // applies timezone offset
    startDateTime.setUTCMinutes(startDateTime.getUTCMinutes() + timezoneOffsetMinutes);
    endDateTime.setUTCMinutes(endDateTime.getUTCMinutes() + timezoneOffsetMinutes);

    // formats to YYYY-MM-DDTHH:mmZ
    const formattedStart = `${startDateTime.toISOString().slice(0, 19)}Z`;
    const formattedEnd = `${endDateTime.toISOString().slice(0, 19)}Z`;
    const req = {
      StartDate: formattedStart,
      EndDate: formattedEnd,
      Partner_Seq: currentPartner.Partner_Seq,
    };

    // get start time
    const startTime = Date.now();
    // fetch the first page
    const firstRes = await fetchReportData({ ...req, Page: 1 });

    if (!firstRes) return;
    let allRecords = firstRes.Records;
    dispatch(setAction({ records: [], startDate, endDate, timezone, loading: true }));

    // fetch all other pages if needed
    if (reportType !== REPORTS.PAYOUTS && firstRes.TotalPage > 1) {
      for (let i = 2; i <= firstRes.TotalPage; i += 1) {
        // Used for lambda concurrency limit: Fetching pages sequentially instead of in parallel
        // to avoid overwhelming the server with simultaneous requests.
        // eslint-disable-next-line no-await-in-loop
        const response = await fetchReportData({ ...req, Page: i });
        if (response === null) return;
        allRecords = allRecords.concat(response.Records);
      }
    }

    // get elapsed time
    const elapsedTime = Date.now() - startTime;
    // get number of days
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    logAmpEvent(events.REPORT_FULLY_LOADED, {
      'Partner Seq': currentPartner.Partner_Seq,
      'Number of Records': allRecords.length,
      'Load Time': elapsedTime,
      'Report Type': reportType,
      'Number of Days': Math.ceil(endMoment.diff(startMoment, 'days', true)),
      'Date Range': `${startDate} - ${endDate} ${timezone}`,
    });

    dispatch(setAction({ records: allRecords, startDate, endDate, timezone, loading: false }));
    setLongLoadTime(false);

    if (allRecords.length === 0) {
      setScreenReaderAlert(t('Reports_NoRecordsFound_ScreenReaderAlert', { startDate, endDate }));
    } else {
      setTimeout(() => { // timeout required to guarantee screen reader alert
        setScreenReaderAlert(t('Reports_RecordsRetrieved_ScreenReaderAlert', { startDate, endDate }));
      }, 0);
    }
  }, [
    call,
    dispatch,
    setAction,
    loadAction,
    currentPartner,
    loading,
    reportType,
  ]);

  useEffect(() => {
    if (dateRange) {
      getReportData(dateRange);
    } else if (reportRecords === null) {
      getReportData(defaultDateRange);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (error) dispatch(clearAction());
  }, [error, dispatch, clearAction]);

  return [reportRecords, loading, longLoadTime, error, getReportData];
}

export default useReportData;
